import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import {
  EUserPermission,
  EUserRole,
} from '@/hooks/usePermissions/permissions.type'
import { usePermissions } from '@/hooks/usePermissions/usePermissions'
import { useRedirectBasedOnPermissions } from '@/hooks/useRedirectBasedOnPermissions'
import { useGetExpiredEnterpriseReportsQuery } from '@/store/api/Enterprise/Enterprise.query'
import {
  selectSelectedTeam,
  selectSelectedWorkStation,
} from '@/store/api/Users/Users.selector'
import {
  setSelectedTeamById,
  setSelectedWorkspaceById,
} from '@/store/api/Users/Users.slice'
import {
  useGetAllWorkspaceReportsQuery,
  useGetWorkspaceScopedExpiredReportsQuery,
  useGetWorkspaceScopedReportsQuery,
} from '@/store/api/Workspace/Workspace.query'
import type { EReportFilter } from '@/store/api/Workspace/Workspace.types'

import { AvailableReportsTab } from './components/AvailableReportsTab'
import { ExpiredReportsTab } from './components/ExpiredReportsTab'
import { ReportsTabTrigger } from './components/ReportsTabTrigger'
import type { ReportStatusType } from './components/ReportStatusSelector'

export type ExtendedReportFilter = EReportFilter | 'your_reports' | undefined

const PAGE_SIZE = 10

export const Reports = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()

  const { hasPermission } = usePermissions()
  const { hasViewReports } = useRedirectBasedOnPermissions()
  hasViewReports()

  const isEnterpriseScope = hasPermission(
    EUserPermission.HANDLE_REPORTS,
    EUserRole.SUPERVISOR,
  )
  const showAllOfficesOption = isEnterpriseScope

  const selectedWorkStation = useAppSelector(selectSelectedWorkStation)
  const selectedTeam = useAppSelector(selectSelectedTeam)

  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  const urlWorkspaceId = urlParams.get('workspaceId')
  const urlTeamId = urlParams.get('teamid')
  const urlSubjectName = urlParams.get('subjectName')

  useEffect(() => {
    if (selectedWorkStation && urlWorkspaceId) {
      dispatch(setSelectedWorkspaceById(urlWorkspaceId))
    }
    if (selectedWorkStation && urlTeamId) {
      dispatch(setSelectedTeamById(urlTeamId))
    }
  }, [urlWorkspaceId, urlTeamId, selectedWorkStation, dispatch])

  const [selectedOfficeAndTeam, setSelectedOfficeAndTeam] = useState<{
    workspace: string | null
    team: string | null
  }>(() => ({
    workspace: isEnterpriseScope ? null : selectedWorkStation?._id ?? null,
    team: isEnterpriseScope ? null : selectedTeam?._id ?? null,
  }))

  /**
   * Users below supervisor always query one concrete workspace/team. The local
   * selection is captured once at mount, and on a cold load straight into
   * /reports (refresh, bookmark) that first render happens before LeftSideBar's
   * effect has dispatched /me into the store, so it started as null and nothing
   * re-synced it: the request went out as /workspaces//teams//subjects, 404,
   * and the page showed "no reports" until the user touched the filters. Fall
   * back to the store's selection, which is what the mount value meant to capture.
   */
  const scopedOfficeAndTeam = isEnterpriseScope
    ? selectedOfficeAndTeam
    : {
        workspace:
          selectedOfficeAndTeam.workspace ?? selectedWorkStation?._id ?? null,
        team: selectedOfficeAndTeam.team ?? selectedTeam?._id ?? null,
      }
  const scopeReady =
    isEnterpriseScope ||
    Boolean(scopedOfficeAndTeam.workspace && scopedOfficeAndTeam.team)

  const [activeTab, setActiveTab] = useState<'available' | 'archived'>(
    'available',
  )

  const [availablePage, setAvailablePage] = useState(1)
  const [availableSearch, setAvailableSearch] = useState(urlSubjectName ?? '')
  const [availableFilter, setAvailableFilter] = useState<
    ExtendedReportFilter | 'all'
  >('all')
  const [availableStatus, setAvailableStatus] =
    useState<ReportStatusType>('all')

  const [archivedPage, setArchivedPage] = useState(1)
  const [archivedSearch, setArchivedSearch] = useState('')

  const availableReportType =
    availableFilter === 'all' || availableFilter === 'your_reports'
      ? undefined
      : availableFilter
  const availableOnlyUser = availableFilter === 'your_reports'
  const availableStatusArg =
    availableStatus === 'all' || availableStatus === 'new'
      ? undefined
      : availableStatus
  const availableSearchArg = availableSearch || undefined

  const availableArgs = {
    workspaceId: scopedOfficeAndTeam.workspace,
    teamId: scopedOfficeAndTeam.team,
    page: availablePage,
    pageSize: PAGE_SIZE,
    reportType: availableReportType,
    onlyUser: availableOnlyUser,
    status: availableStatusArg,
    searchByString: availableSearchArg,
  }

  const availableEnterpriseQuery = useGetAllWorkspaceReportsQuery(
    availableArgs as Parameters<typeof useGetAllWorkspaceReportsQuery>[0],
    { skip: !isEnterpriseScope },
  )
  const availableWorkspaceQuery = useGetWorkspaceScopedReportsQuery(
    availableArgs as Parameters<typeof useGetWorkspaceScopedReportsQuery>[0],
    { skip: isEnterpriseScope || !scopeReady },
  )
  const availableQuery = isEnterpriseScope
    ? availableEnterpriseQuery
    : availableWorkspaceQuery

  const archivedArgs = {
    workspaceId: scopedOfficeAndTeam.workspace,
    teamId: scopedOfficeAndTeam.team,
    page: archivedPage,
    pageSize: PAGE_SIZE,
    searchByString: archivedSearch || undefined,
  }

  const archivedEnterpriseQuery = useGetExpiredEnterpriseReportsQuery(
    archivedArgs,
    { skip: !isEnterpriseScope },
  )
  const archivedWorkspaceQuery = useGetWorkspaceScopedExpiredReportsQuery(
    archivedArgs,
    { skip: isEnterpriseScope || !scopeReady },
  )
  const archivedQuery = isEnterpriseScope
    ? archivedEnterpriseQuery
    : archivedWorkspaceQuery

  const availableCount = availableQuery.currentData?.totalItems
  const archivedCount = archivedQuery.currentData?.totalItems

  /**
   * The page number is part of the request but not of the cache key, and it has
   * to change in the same update as whatever else changed. Resetting it in an
   * effect left one render where the new search travelled with the old page
   * number: the list fetched, say, page 2 of "ma" — a request for a page the
   * user never asked for — and then page 1 a moment later, with both answers
   * racing to land in the same cache entry. Whichever arrived second decided
   * what the list held, and the auto-fill had already spent itself on the first.
   */
  const changeAvailableSearch = (value: string) => {
    setAvailableSearch(value)
    setAvailablePage(1)
  }

  const changeArchivedSearch = (value: string) => {
    setArchivedSearch(value)
    setArchivedPage(1)
  }

  const handleApplyAvailableFilters = (filters: {
    workspace: string | null
    team: string | null
    reportType?: ExtendedReportFilter | 'all'
    status?: ReportStatusType
  }) => {
    setSelectedOfficeAndTeam({
      workspace: filters.workspace,
      team: filters.team,
    })
    if (filters.reportType !== undefined) setAvailableFilter(filters.reportType)
    if (filters.status !== undefined) setAvailableStatus(filters.status)
    setAvailablePage(1)
    setArchivedPage(1)
  }

  const handleApplyArchivedFilters = (filters: {
    workspace: string | null
    team: string | null
  }) => {
    setSelectedOfficeAndTeam({
      workspace: filters.workspace,
      team: filters.team,
    })
    setAvailablePage(1)
    setArchivedPage(1)
  }

  return (
    <WeScreen useHeader={false} contentClassName="gap-0">
      <div className="bg-baseColorHighlight rounded-[24px] px-4 py-6 flex-1 min-h-0 flex flex-col gap-5">
        <Tabs
          value={activeTab}
          onValueChange={(val) => setActiveTab(val as 'available' | 'archived')}
          className="flex-1 min-h-0 flex flex-col gap-5"
        >
          <TabsList className="w-fit bg-baseColor justify-start p-1 rounded-xl mx-3">
            <TabsTrigger
              value="available"
              className="hover:cursor-pointer rounded-xl !border-0 bg-baseColor z-10 !h-auto !px-3 !sm:px-3 py-1.5 text-sm font-medium text-neutralColor60 data-[state=active]:bg-baseColorHighlight data-[state=active]:text-neutralColor data-[state=active]:font-semibold data-[state=active]:shadow-none"
            >
              <ReportsTabTrigger
                label={t('dashboard.reportsScreen.tabs.available')}
                count={availableCount}
              />
            </TabsTrigger>
            <TabsTrigger
              value="archived"
              className="hover:cursor-pointer rounded-xl !border-0 bg-baseColor z-10 !h-auto !px-3 !sm:px-3 py-1.5 text-sm font-medium text-neutralColor60 data-[state=active]:bg-baseColorHighlight data-[state=active]:text-neutralColor data-[state=active]:font-semibold data-[state=active]:shadow-none"
            >
              <ReportsTabTrigger
                label={t('dashboard.reportsScreen.tabs.archived')}
                count={archivedCount}
              />
            </TabsTrigger>
          </TabsList>

          {activeTab === 'available' && (
            <TabsContent
              value="available"
              forceMount
              className="flex-1 min-h-0 flex flex-col gap-5 mt-0"
            >
              <AvailableReportsTab
                data={availableQuery.data}
                isFetching={availableQuery.isFetching}
                loadNextPage={() => setAvailablePage((p) => p + 1)}
                selectedOfficeAndTeam={scopedOfficeAndTeam}
                onApplyFilters={handleApplyAvailableFilters}
                search={availableSearch}
                onSearchChange={changeAvailableSearch}
                filter={availableFilter}
                status={availableStatus}
                showAllOfficesOption={showAllOfficesOption}
                autoFetchThreshold={PAGE_SIZE * 2}
              />
            </TabsContent>
          )}

          {activeTab === 'archived' && (
            <TabsContent
              value="archived"
              forceMount
              className="flex-1 min-h-0 flex flex-col gap-5 mt-0"
            >
              <ExpiredReportsTab
                data={archivedQuery.data}
                isFetching={archivedQuery.isFetching}
                loadNextPage={() => setArchivedPage((p) => p + 1)}
                selectedOfficeAndTeam={scopedOfficeAndTeam}
                onApplyFilters={handleApplyArchivedFilters}
                search={archivedSearch}
                onSearchChange={changeArchivedSearch}
                showAllOfficesOption={showAllOfficesOption}
                autoFetchThreshold={PAGE_SIZE * 2}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </WeScreen>
  )
}
